import { Flex } from 'antd';
import React from 'react';
import { AiPanelCardModel, AiPanelContract } from '@tools/shared';
import {
  IconSize,
  IconType,
  IconWrap,
  semanticTheme,
  Typography
} from '@tools/ui/dime';
import datesUtil from '@tools/ui-utils/dates.util';
import AiPanel, { AiPanelCard } from './AiPanel';
import { AiPanelBlockView, AiPanelLinkView } from './AiPanelBlock';
import { getIconType, getIconVariant, getToneColor } from './aiPanelIcons';

type Props = {
  contract: AiPanelContract;
  onClose: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  isLoading?: boolean;
  isNoData?: boolean;
  onFeedback?: (
    helpful: boolean,
    message?: string
  ) => Promise<boolean> | boolean;
  hasAnimated?: boolean;
  onAnimationStart?: () => void;
};

export const buildAiPanelCards = (
  cards: AiPanelCardModel[]
): React.ReactNode[] =>
  cards.map((card, i) => {
    const hasHeader =
      !!card.title ||
      !!card.titleLink ||
      !!getIconType(card.titleIcon) ||
      !!card.headerAction;

    return (
      <AiPanelCard key={i} $transparent={card.noBackground}>
        {!!hasHeader && (
          <Flex align="center" justify="space-between">
            <Flex align="center" gap={semanticTheme.dime.spacing.s}>
              {!!card.title && (
                <Typography variant="body1Bold">{card.title}</Typography>
              )}
              {!!getIconType(card.titleIcon) && (
                <IconWrap
                  type={getIconType(card.titleIcon) as IconType}
                  variant={getIconVariant(card.titleIcon)}
                  size={IconSize.Small}
                  style={{ color: getToneColor(card.titleIconTone) }}
                />
              )}
            </Flex>
            <Flex align="center" gap={semanticTheme.dime.spacing.s}>
              {!!card.titleLink && <AiPanelLinkView link={card.titleLink} />}
              {!!card.headerAction && (
                <AiPanelLinkView link={card.headerAction} />
              )}
            </Flex>
          </Flex>
        )}
        {card.blocks.map((block, j) => (
          <AiPanelBlockView key={j} block={block} />
        ))}
        {!!card.extraLink && <AiPanelLinkView link={card.extraLink} />}
      </AiPanelCard>
    );
  });

const AiPanelContent: React.FC<Props> = ({
  contract,
  onClose,
  onRefresh,
  isRefreshing,
  isLoading,
  isNoData,
  onFeedback,
  hasAnimated,
  onAnimationStart
}) => (
  <AiPanel
    title={contract.title}
    subtitle={contract.subtitle}
    introMessage={contract.introMessage}
    updatedAt={
      contract.updatedAt
        ? `Updated ${datesUtil.formatDate(
            contract.updatedAt,
            'MMM D, YYYY ,HH:mm:ss'
          )}`
        : undefined
    }
    feedbackPrompt={contract.feedbackPrompt}
    cards={buildAiPanelCards(contract.cards)}
    onClose={onClose}
    onRefresh={onRefresh}
    isRefreshing={isRefreshing}
    isLoading={isLoading}
    isNoData={isNoData}
    onFeedback={onFeedback}
    hasAnimated={hasAnimated}
    onAnimationStart={onAnimationStart}
  />
);

export default AiPanelContent;
